import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton } from '@mui/material';
import { v4 as uuidv4 } from 'uuid';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import { useTheme } from '@mui/material/styles';
import { AddButtonUi, CheckboxUI, InputUI, OrderArrowsUI } from '../../Interface';

const CheckListItemTable = ({ items, setItems, user, disabled }) => {
  const theme = useTheme();
  const ui = theme.palette.ui;

  const [editingRows, setEditingRows] = useState(new Set());
  const [editingValues, setEditingValues] = useState({});

  const handleDelete = (id) => {
    const updatedItems = items.filter((item) => item.id !== id);
    setItems(updatedItems);
    setEditingRows((prev) => new Set([...prev].filter((rowId) => rowId !== id)));
    setEditingValues((prev) => {
      const { [id]: _, ...rest } = prev;
      return rest;
    });
  };

  const handleEdit = (id) => {
    const itm = items.find((item) => item.id === id);
    setEditingRows((prev) => new Set([...prev, id]));
    setEditingValues((prev) => ({
      ...prev,
      [id]: {
        name: itm?.name || '',
        descriptionName: itm?.descriptionName || null,
        attachRequired: !!itm?.attachRequired,
        descriptionRequired: !!itm?.descriptionRequired,
      },
    }));
  };

  const handleEditSave = (id) => {
    const draft = editingValues[id];
    if (!draft) return;
    if (!String(draft.name ?? '').trim()) return;

    const updatedItems = items.map((item) =>
      item.id === id
        ? {
            ...item,
            name: draft.name,
            descriptionName: draft.descriptionName,
            attachRequired: !!draft.attachRequired,
            descriptionRequired: !!draft.descriptionRequired,
          }
        : item
    );
    setItems(updatedItems);
    setEditingRows((prev) => new Set([...prev].filter((rowId) => rowId !== id)));
    setEditingValues((prev) => ({ ...prev, [id]: undefined }));
  };

  const handleAddNewItem = () => {
    const newVisOrder = (items.length || 0) + 1;
    const newItem = {
      id: uuidv4(),
      name: '',
      createdBy: user.userId,
      visOrder: newVisOrder,
      attachRequired: false,
      descriptionRequired: false,
      descriptionName: null,
    };
    setItems((prevItems) => [...prevItems, newItem]);
    setEditingRows((prev) => new Set([...prev, newItem.id]));
    setEditingValues((prev) => ({
      ...prev,
      [newItem.id]: {
        name: '',
        descriptionName: null,
        attachRequired: false,
        descriptionRequired: false,
      },
    }));
  };

  const handleOrderChange = (id, currentOrder, direction) => {
    const newOrder = direction === 'up' ? currentOrder - 1 : currentOrder + 1;

    if (newOrder < 1 || newOrder > items.length) return;

    const updatedItems = items.map((item) => {
      if (item.id === id) {
        return { ...item, visOrder: newOrder };
      } else if (item.visOrder === newOrder) {
        return { ...item, visOrder: currentOrder };
      }
      return item;
    });

    const sortedItems = updatedItems.sort((a, b) => a.visOrder - b.visOrder);

    setItems(sortedItems);
  };

  return (
    <div>
      <div className="mb-4">
        <AddButtonUi onClick={handleAddNewItem} disabled={disabled} />
      </div>
      <TableContainer component={Paper} className="h-[350px] custom-scrollbar">
        <Table stickyHeader>
          <TableHead
            sx={{
              backgroundColor: ui.tableHeader,
              '& .MuiTableCell-root': {
                backgroundColor: ui.tableHeader,
              },
            }}
          >
            <TableRow>
              <TableCell style={{ width: '300px' }}>Checklista neve</TableCell>
              <TableCell>Csatolt fájlt kötelező</TableCell>
              <TableCell>Leírás kötelező</TableCell>
              <TableCell>Leírás megnevezés</TableCell>
              <TableCell>Sorrend</TableCell>
              <TableCell>Szerkesztés</TableCell>
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              '& .MuiTableCell-body': {
                padding: '10px 0px 10px 15px',
              },
            }}
          >
            {items.map((item, index) => {
              const isEditing = editingRows.has(item.id);
              const draft = editingValues[item.id] || {};
              return (
                <TableRow key={item.id} className={`${index % 2 === 0 ? 'bg-gray-100' : 'bg-white'}`}>
                  <TableCell style={{ width: '300px' }}>
                    {isEditing ? (
                      <InputUI
                        value={draft.name ?? ''}
                        onChange={(e) =>
                          setEditingValues((prev) => ({
                            ...prev,
                            [item.id]: { ...(prev[item.id] || {}), name: e.target.value },
                          }))
                        }
                        variant="standard"
                      />
                    ) : (
                      item.name
                    )}
                  </TableCell>
                  <TableCell sx={{ width: '170px' }}>
                    <CheckboxUI
                      checked={isEditing ? !!draft.attachRequired : !!item.attachRequired}
                      onChange={(e) =>
                        setEditingValues((prev) => ({
                          ...prev,
                          [item.id]: { ...(prev[item.id] || {}), attachRequired: e.target.checked },
                        }))
                      }
                      disabled={!isEditing}
                    />
                  </TableCell>
                  <TableCell sx={{ width: '130px' }}>
                    <CheckboxUI
                      checked={isEditing ? !!draft.descriptionRequired : !!item.descriptionRequired}
                      onChange={(e) =>
                        setEditingValues((prev) => ({
                          ...prev,
                          [item.id]: { ...(prev[item.id] || {}), descriptionRequired: e.target.checked },
                        }))
                      }
                      disabled={!isEditing}
                    />
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <InputUI
                        value={draft.descriptionName ?? ''}
                        onChange={(e) =>
                          setEditingValues((prev) => ({
                            ...prev,
                            [item.id]: { ...(prev[item.id] || {}), descriptionName: e.target.value },
                          }))
                        }
                        variant="standard"
                      />
                    ) : (
                      item.descriptionName
                    )}
                  </TableCell>
                  <TableCell>
                    <OrderArrowsUI
                      onUpClick={() => handleOrderChange(item.id, item.visOrder, 'up')}
                      onDownClick={() => handleOrderChange(item.id, item.visOrder, 'down')}
                    />
                  </TableCell>
                  <TableCell>
                    {isEditing ? (
                      <IconButton sx={{ margin: 0, padding: 0 }}>
                        <CheckIcon onClick={() => handleEditSave(item.id)} />
                      </IconButton>
                    ) : (
                      <IconButton sx={{ margin: 0, padding: 0 }}>
                        <EditIcon onClick={() => handleEdit(item.id)} />
                      </IconButton>
                    )}
                    <IconButton sx={{ margin: 0, padding: 0, marginLeft: '0.5rem' }}>
                      <DeleteIcon onClick={() => handleDelete(item.id)} />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    </div>
  );
};

export default CheckListItemTable;
